<template>
    <f7-list-item class="list-item-input" :class="{ 'item-input-focused': isFocused, 'item-input-invalid': !isValid }">
        <f7-list-input v-if="$props['floating-label']" :floating-label="$props['floating-label']" :label="label" :outline="outline"
            :readonly="true" :class="{ 'input-invalid': !isValid }" style="pointer-events: none; position: absolute; opacity: 0; z-index: -1;" />
        <template v-slot:inner>
            <!-- Contenedor de badges -->
            <div class="badge-list-container" :class="{ 'outline-style': outline, 'has-floating-label': $props['floating-label'], 'has-values': hasValues }">
                <!-- Floating label -->
                <div v-if="$props['floating-label']" class="floating-label-text" :class="{ 'label-up': hasValues || isFocused }">
                    {{ label }}
                </div>
                
                <div class="badge-list-content">
                    <!-- Label para cuando no hay floating label -->
                    <div v-if="!$props['floating-label'] && label" class="badge-list-label">{{ label }}</div>

                    <!-- Lista de badges -->
                    <div class="badge-list-items">
                        <f7-chip v-for="(item, index) in currentValue" :key="`badge-${index}`" :text="getDisplayText(item)" deleteable
                            @delete="removeItem(index)" class="badge-item" :color="getBadgeColor(item)"></f7-chip>
                    </div>
                </div>

                <!-- Botón de añadir -->
                <f7-button v-if="!readonly" small icon-f7="plus" @click="showAddDialog" class="add-button-main"
                    color="blue"></f7-button>
            </div>

            <!-- Mensaje de error -->
            <div v-if="!isValid && errorMessage" class="item-input-error-message">
                {{ errorMessage }}
            </div>
        </template>
    </f7-list-item>
</template>

<style scoped>
.badge-list-container {
    width: 100%;
    min-height: 32px;
    padding: 4px 12px;
    border-radius: 4px;
    background-color: transparent;
    position: relative;
    display: flex;
    flex-direction: column;
}

.badge-list-container.has-floating-label {
    padding-top: 16px;
    padding-bottom: 4px;
}

.badge-list-container.outline-style {
    border: 1px solid #c7c7cc;
    background-color: #fff;
}

.badge-list-container.outline-style:focus-within {
    border-color: #007aff;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
}

.floating-label-text {
    position: absolute;
    left: 12px;
    top: 12px;
    font-size: 16px;
    color: #8e8e93;
    transition: all 0.2s ease;
    pointer-events: none;
    z-index: 2;
    background-color: transparent;
    padding: 0 4px;
}

.badge-list-container.outline-style .floating-label-text {
    background-color: #fff;
}

.floating-label-text.label-up {
    top: -8px;
    font-size: 12px;
    color: #007aff;
    font-weight: 500;
}

.badge-list-container.has-floating-label.has-values .floating-label-text {
    top: -8px;
    font-size: 12px;
    color: #8e8e93;
    font-weight: 500;
}

.badge-list-container.outline-style:focus-within .floating-label-text {
    color: #007aff;
}

.badge-list-content {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.badge-list-label {
    font-size: 12px;
    color: #8e8e93;
    margin-bottom: 4px;
    font-weight: 500;
}

.badge-list-items {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    min-height: 24px;
    margin-top: 2px;
}

.badge-item {
    margin: 0;
}

.add-button-main {
    height: 24px;
    width: 24px;
    min-width: 24px;
    margin: 0;
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    --f7-button-padding-horizontal: 0px;
}

/* Estados de validación */
.item-input-invalid .badge-list-container.outline-style {
    border-color: #ff3b30;
}

.item-input-invalid .badge-list-container.outline-style:focus-within {
    border-color: #ff3b30;
    box-shadow: 0 0 0 2px rgba(255, 59, 48, 0.2);
}

.item-input-invalid .floating-label-text {
    color: #ff3b30 !important;
}

.item-input-error-message {
    font-size: 12px;
    color: #ff3b30;
    margin-top: 4px;
    padding-left: 12px;
}

/* Responsive */
@media (max-width: 480px) {
    .badge-list-items {
        gap: 4px;
    }
}

/* Theme support */
.theme-dark .badge-list-container.outline-style {
    border-color: #48484a;
    background-color: #1c1c1e;
}

.theme-dark .badge-list-container.outline-style .floating-label-text {
    background-color: #1c1c1e;
}

.theme-dark .floating-label-text {
    color: #8e8e93;
}

.theme-dark .floating-label-text.label-up {
    color: #007aff;
}

.theme-dark .badge-list-label {
    color: #8e8e93;
}
</style>

<script>
export default {
    name: 'rInputBadge',
    props: {
        form: {
            type: Object,
            required: true
        },
        name: {
            type: String,
            required: true
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        'floating-label': {
            type: Boolean,
            default: false
        },
        outline: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        validation: {
            type: Object,
            default: () => ({
                isValid: () => true,
                errorMessage: ''
            })
        },
        separator: {
            type: String,
            default: ','
        },
        allowDuplicates: {
            type: Boolean,
            default: false
        },
        maxItems: {
            type: Number,
            default: null
        },
        inputType: {
            type: String,
            default: 'text',
            validator: (value) => ['text', 'number', 'email'].includes(value)
        },
        delimiter: {
            type: String,
            default: null
        },
        textProperty: {
            type: String,
            default: null
        },
        valueProperty: {
            type: String,
            default: null
        },
        badgeColor: {
            type: [String, Function],
            default: null
        },
        objectMode: {
            type: Boolean,
            default: false
        },
        // Valores sugeridos. Si se informan, el botón "+" abre un selector con ellos
        // en vez del prompt de texto libre, que sigue disponible como "Otro…".
        predefinidos: {
            type: Array,
            default: () => []
        },
        // Normaliza a mayúsculas al añadir. Necesario cuando el valor se usa luego como
        // filtro exacto: los tags de documentos se filtran así (distinguen mayúsculas).
        uppercase: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isFocused: false,
            isValid: true,
            errorMessage: ''
        };
    },
    computed: {
        hasValues() {
            return this.currentValue && this.currentValue.length > 0;
        },
        currentValue: {
            get() {
                const value = this.form.formData[this.name];
                if (Array.isArray(value)) {
                    return value;
                }
                if (typeof value === 'string' && value.trim()) {
                    return value.split(this.separator).map(item => item.trim()).filter(item => item);
                }
                return [];
            },
            set(value) {
                this.$set(this.form.formData, this.name, value);
                this.validateField();
            }
        }
    },
    methods: {
        showAddDialog() {
            // Con valores predefinidos se ofrece el selector; sin ellos, el prompt de siempre.
            if (this.predefinidos && this.predefinidos.length) {
                this.showSelectorPredefinidos();
                return;
            }
            this.showPromptDialog();
        },

        showSelectorPredefinidos() {
            const self = this;
            const puestos = this.currentValue.map(item => String(this.getDisplayText(item)).toUpperCase());
            const disponibles = this.predefinidos
                .map(valor => String(valor))
                .filter(valor => puestos.indexOf(valor.toUpperCase()) < 0);

            const buttons = disponibles.map(valor => ({
                text: valor,
                onClick() {
                    self.addItem(valor);
                }
            }));

            buttons.push({
                text: 'Otro…',
                onClick() {
                    self.showPromptDialog();
                }
            });
            buttons.push({ text: 'Cancelar', color: 'red' });

            this.$f7.actions.create({ buttons: [buttons] }).open();
        },

        normalizarValor(value) {
            const limpio = String(value === undefined || value === null ? '' : value).trim();
            return this.uppercase ? limpio.toUpperCase() : limpio;
        },

        esPredefinido(item) {
            if (!this.predefinidos || !this.predefinidos.length) {
                return false;
            }
            const texto = String(this.getDisplayText(item)).toUpperCase();
            return this.predefinidos.some(valor => String(valor).toUpperCase() === texto);
        },

        showPromptDialog() {
            const self = this;

            try {
                const dialogTitle = this.placeholder || this.$t('common.anadir_valor');
                const dialogText = this.getDialogText();
                
                this.$f7.dialog.prompt(
                    dialogText,
                    dialogTitle,
                    (value) => {
                        try {
                            if (value && value.trim()) {
                                const result = self.addItem(value.trim());
                                
                                // Siempre continuar el flujo, independientemente del resultado
                                // Solo mostrar mensaje si hay información relevante pero sin bloquear el flujo
                                if (result.message && (result.message.includes('duplicado') || result.message.includes('eliminado') || !result.success)) {
                                    // Mostrar toast en lugar de alert para no interrumpir el flujo
                                    self.$f7.toast.create({
                                        text: result.message,
                                        position: 'center',
                                        closeTimeout: 2000
                                    }).open();
                                }
                                
                                // Continuar con el flujo sin interrupciones
                                setTimeout(() => {
                                    self.showPromptDialog();
                                }, 100);
                            } else {
                                // Si el valor está vacío, continuar sin hacer nada
                                setTimeout(() => {
                                    self.showPromptDialog();
                                }, 100);
                            }
                        } catch (error) {
                            console.error('Error en showAddDialog callback:', error);
                            // Continuar el flujo incluso si hay error
                            setTimeout(() => {
                                self.showPromptDialog();
                            }, 100);
                        }
                    },
                    () => {
                        // Cancelado - no hacer nada, el flujo se detiene aquí
                    },
                    '' // valor por defecto
                );
            } catch (error) {
                console.error('Error en showAddDialog:', error);
                // Si hay error, mostrar toast y no continuar
                this.$f7.toast.create({
                    text: 'Error inesperado en el diálogo',
                    position: 'center',
                    closeTimeout: 2000
                }).open();
            }
        },        getDialogText() {
            switch (this.inputType) {
                case 'number':
                    return this.$t('common.ingrese_numero');
                case 'email':
                    return this.$t('common.ingrese_email');
                default:
                    return this.$t('common.ingrese_valor');
            }
        },

        addItem(value) {
            try {
                if (!value || !value.trim()) {
                    return { success: true, message: 'Valor vacío omitido' };
                }
                
                const trimmedValue = this.normalizarValor(value);

                // Si hay un delimitador definido, procesar múltiples valores
                if (this.delimiter) {
                    return this.addMultipleItems(trimmedValue);
                }
                
                // Procesar un solo valor
                return this.addSingleItem(trimmedValue);
            } catch (error) {
                console.error('Error en addItem:', error);
                return { success: true, message: 'Error procesando valor, continuando...' };
            }
        },

        addSingleItem(value) {
            // En modo objeto, crear el objeto con el valor en la propiedad especificada
            if (this.objectMode && this.valueProperty) {
                const newObject = {};
                newObject[this.valueProperty] = value;
                
                // Verificar tipo de input para el valor
                if (this.inputType === 'number' && isNaN(value)) {
                    const message = this.$t('common.valor_numerico_requerido') || 'Se requiere un valor numérico';
                    return { success: false, message };
                }
                
                if (this.inputType === 'email' && !this.isValidEmail(value)) {
                    const message = this.$t('common.email_invalido') || 'Email inválido';
                    return { success: false, message };
                }
                
                // Verificar límite máximo
                if (this.maxItems && this.currentValue.length >= this.maxItems) {
                    const message = this.$t('common.maximo_items_alcanzado', { max: this.maxItems }) || `Máximo ${this.maxItems} elementos permitidos`;
                    return { success: false, message };
                }
                
                // Verificar duplicados basado en el valueProperty
                if (!this.allowDuplicates) {
                    const isDuplicate = this.currentValue.some(item => {
                        if (typeof item === 'object' && item !== null) {
                            return item[this.valueProperty] === value;
                        }
                        return item === value;
                    });
                    
                    if (isDuplicate) {
                        const message = `"${value}" ya existe, duplicado omitido`;
                        return { success: true, message };
                    }
                }
                
                // Añadir el nuevo objeto
                const newValue = [...this.currentValue, newObject];
                this.currentValue = newValue;
                this.clearError();
                
                // Emit change event
                this.$emit('change', newValue);
                this.$emit('add-item', newObject);
                
                return { success: true, message: `"${value}" añadido correctamente` };
            }
            
            // Modo normal (string/number)
            // Validar tipo de input
            if (this.inputType === 'number' && isNaN(value)) {
                const message = this.$t('common.valor_numerico_requerido') || 'Se requiere un valor numérico';
                return { success: false, message };
            }
            
            if (this.inputType === 'email' && !this.isValidEmail(value)) {
                const message = this.$t('common.email_invalido') || 'Email inválido';
                return { success: false, message };
            }
            
            // Verificar límite máximo
            if (this.maxItems && this.currentValue.length >= this.maxItems) {
                const message = this.$t('common.maximo_items_alcanzado', { max: this.maxItems }) || `Máximo ${this.maxItems} elementos permitidos`;
                return { success: false, message };
            }
            
            // Verificar duplicados - pero siempre retornar éxito
            if (!this.allowDuplicates && this.currentValue.includes(value)) {
                // No hacer nada, pero reportar como éxito para no interrumpir el flujo
                const message = `"${value}" ya existe, duplicado omitido`;
                return { success: true, message };
            }
            
            // Añadir el nuevo valor
            const newValue = [...this.currentValue, value];
            this.currentValue = newValue;
            this.clearError();
            
            // Emit change event
            this.$emit('change', newValue);
            this.$emit('add-item', value);
            
            return { success: true, message: `"${value}" añadido correctamente` };
        },

        addMultipleItems(value) {
            try {
                // Crear expresión regular con el delimitador
                const delimiterRegex = new RegExp(this.delimiter);
                
                // Dividir el valor usando el delimitador
                const values = value.split(delimiterRegex)
                    .map(item => this.normalizarValor(item))
                    .filter(item => item.length > 0);
                
                if (values.length === 0) {
                    return { success: false, message: this.$t('common.valor_requerido') || 'Valor requerido' };
                }
                
                const results = {
                    added: [],
                    skipped: [],
                    errors: [],
                    duplicatesRemoved: []
                };
                
                // Crear una lista de valores únicos, manteniendo el orden
                const uniqueValues = [];
                const seenValues = new Set([...this.currentValue]); // Valores ya existentes
                
                // Procesar cada valor
                for (const singleValue of values) {
                    // Validar tipo de input
                    if (this.inputType === 'number' && isNaN(singleValue)) {
                        results.errors.push(`"${singleValue}" no es un número válido`);
                        continue;
                    }
                    
                    if (this.inputType === 'email' && !this.isValidEmail(singleValue)) {
                        results.errors.push(`"${singleValue}" no es un email válido`);
                        continue;
                    }
                    
                    // Verificar límite máximo
                    if (this.maxItems && (this.currentValue.length + uniqueValues.length) >= this.maxItems) {
                        results.errors.push(`Límite máximo de ${this.maxItems} elementos alcanzado`);
                        break;
                    }
                    
                    // Gestionar duplicados
                    if (seenValues.has(singleValue)) {
                        // Si ya existe (en valores actuales o en los que estamos procesando)
                        results.duplicatesRemoved.push(singleValue);
                    } else {
                        // Valor único, añadirlo
                        uniqueValues.push(singleValue);
                        seenValues.add(singleValue);
                        results.added.push(singleValue);
                    }
                }
                
                // Añadir los valores únicos válidos
                if (results.added.length > 0) {
                    const newValue = [...this.currentValue, ...uniqueValues];
                    this.currentValue = newValue;
                    this.clearError();
                    
                    // Emit change event
                    this.$emit('change', newValue);
                    this.$emit('add-items', results.added);
                }
                
                // Generar mensaje de resultado
                return this.generateMultipleItemsMessage(results);
                
            } catch (error) {
                const message = `Error en expresión regular del delimitador: ${error.message}`;
                this.showError(message);
                return { success: false, message };
            }
        },

        generateMultipleItemsMessage(results) {
            const messages = [];
            
            if (results.added.length > 0) {
                messages.push(`${results.added.length} elemento(s) añadido(s)`);
            }
            
            if (results.duplicatesRemoved && results.duplicatesRemoved.length > 0) {
                messages.push(`${results.duplicatesRemoved.length} duplicado(s) eliminado(s)`);
            }
            
            if (results.errors.length > 0) {
                messages.push(`${results.errors.length} error(es)`);
            }
            
            // Determinar si es éxito:
            // - Si se añadió al menos un elemento: éxito
            // - Si solo hay duplicados (sin errores): también éxito (para mantener flujo)
            // - Solo es error si hay errores reales de validación
            const hasOnlyDuplicates = results.added.length === 0 && 
                                     results.duplicatesRemoved.length > 0 && 
                                     results.errors.length === 0;
            
            const success = results.added.length > 0 || hasOnlyDuplicates;
            
            let message;
            if (results.added.length === 0 && results.duplicatesRemoved.length > 0) {
                // Solo duplicados
                message = `Todos los valores ya existen (${results.duplicatesRemoved.length} duplicados omitidos)`;
            } else {
                message = messages.join(', ');
            }
            
            // Si hay duplicados eliminados y también elementos añadidos
            if (results.duplicatesRemoved && results.duplicatesRemoved.length > 0 && results.added.length > 0) {
                message += '. Los duplicados fueron eliminados automáticamente';
            }
            
            if (!success && results.errors.length > 0) {
                // Solo mostrar error si hay errores reales de validación
                return { success: false, message: results.errors[0] };
            }
            
            return { success: true, message };
        },        removeItem(index) {
            if (this.readonly) return;

            const removedItem = this.currentValue[index];
            const newValue = this.currentValue.filter((_, i) => i !== index);
            this.currentValue = newValue;
            this.clearError();

            // Emit change event
            this.$emit('change', newValue);
            this.$emit('remove-item', removedItem, index);
        },

        validateField() {
            if (this.validation && typeof this.validation.isValid === 'function') {
                this.isValid = this.validation.isValid(this.currentValue);
                this.errorMessage = this.isValid ? '' : (this.validation.errorMessage || '');
            } else {
                this.isValid = true;
                this.errorMessage = '';
            }
            return this.isValid;
        },

        showError(message) {
            this.isValid = false;
            this.errorMessage = message;
            setTimeout(() => {
                this.clearError();
            }, 3000);
        },

        clearError() {
            this.isValid = true;
            this.errorMessage = '';
        },

        isValidEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        },

        getDisplayText(item) {
            // Si el item es un objeto y hay una propiedad especificada
            if (typeof item === 'object' && item !== null && this.textProperty) {
                return item[this.textProperty] || JSON.stringify(item);
            }
            // Si el item es un objeto pero no hay propiedad especificada, intentar propiedades comunes
            if (typeof item === 'object' && item !== null) {
                // Para objetos de factura, usar codigo preferiblemente
                if (item.codigo) return item.codigo;
                if (item.numero && item.tipo) return `${item.numero} (${item.tipo})`;
                if (item.numero) return item.numero.toString();
                // Propiedades comunes
                if (item.nombre) return item.nombre;
                if (item.text) return item.text;
                if (item.label) return item.label;
                if (item.id) return item.id;
                // Si no tiene propiedades conocidas, usar JSON
                return JSON.stringify(item);
            }
            // Si es string o número, devolver como está
            return item?.toString() || '';
        },

        getBadgeColor(item) {
            // Si badgeColor es una función, ejecutarla con el item
            if (typeof this.badgeColor === 'function') {
                return this.badgeColor(item);
            }
            
            // Si hay color específico definido como string, usarlo
            if (typeof this.badgeColor === 'string') {
                return this.badgeColor;
            }
            
            // Si el item es un objeto, intentar obtener color basado en propiedades
            if (typeof item === 'object' && item !== null) {
                // Para facturas, usar color basado en tipo
                if (item.tipo) {
                    switch (item.tipo.toLowerCase()) {
                        case 'factura':
                            return 'blue';
                        case 'abono':
                            return 'orange';
                        default:
                            return null;
                    }
                }
                // Si tiene propiedad color
                if (item.color) {
                    return item.color;
                }
            }
            
            // Los valores predefinidos se distinguen a simple vista del texto libre.
            if (this.esPredefinido(item)) {
                return 'blue';
            }

            return null; // Sin color específico
        },

        // Métodos públicos para uso externo
        clear() {
            this.currentValue = [];
            this.clearError();
        },

        addItems(items) {
            if (Array.isArray(items)) {
                items.forEach(item => {
                    this.addSingleItem(item);
                });
            } else if (typeof items === 'string') {
                this.addItem(items);
            }
        },

        getValue() {
            return this.currentValue;
        },

        getValueAsStrings() {
            // Método para obtener solo los valores como strings (útil para compatibilidad)
            if (this.objectMode && this.valueProperty) {
                return this.currentValue.map(item => {
                    if (typeof item === 'object' && item !== null) {
                        return item[this.valueProperty];
                    }
                    return item;
                });
            }
            return this.currentValue;
        },

        setValue(value) {
            if (Array.isArray(value)) {
                this.currentValue = value;
            } else if (typeof value === 'string') {
                if (this.objectMode && this.valueProperty) {
                    // En modo objeto, convertir cada string a un objeto
                    const items = value.split(this.separator).map(item => item.trim()).filter(item => item);
                    this.currentValue = items.map(item => {
                        const obj = {};
                        obj[this.valueProperty] = item;
                        return obj;
                    });
                } else {
                    this.currentValue = value.split(this.separator).map(item => item.trim()).filter(item => item);
                }
            }
        }
    },
    watch: {
        'form.formData': {
            handler() {
                this.validateField();
            },
            deep: true
        }
    },
    mounted() {
        this.validateField();
    }
};
</script>
